import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS, ROLE_DEFAULTS } from "@/lib/permissions";
import {
  PlusIcon,
  TrashIcon,
  PencilSimpleIcon,
  UserIcon,
  ShieldIcon,
  XIcon,
} from "@phosphor-icons/react";

export default function UsersManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "partner", permissions: ROLE_DEFAULTS.partner });

  const load = useCallback(async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (u = null) => {
    setEditing(u);
    setForm(
      u
        ? { email: u.email, password: "", name: u.name, role: u.role, permissions: (u.permissions?.length ? u.permissions : ROLE_DEFAULTS[u.role]) || [] }
        : { email: "", password: "", name: "", role: "partner", permissions: ROLE_DEFAULTS.partner }
    );
    setShow(true);
  };

  const changeRole = (role) => {
    setForm((f) => ({ ...f, role, permissions: ROLE_DEFAULTS[role] || [] }));
  };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Ενημερώθηκε");
      } else {
        await api.post("/users", form);
        toast.success("Δημιουργήθηκε");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  const del = async (u) => {
    if (u.id === me?.id) return;
    if (!window.confirm(`Διαγραφή «${u.email}»;`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  if (me?.role !== "admin") {
    return (
      <div className="border border-black/10 p-16 text-center" data-testid="users-no-access">
        <ShieldIcon size={48} weight="thin" className="mx-auto text-[#4B5563]" />
        <div className="heading text-xl font-bold mt-3">Πρόσβαση μόνο για διαχειριστές</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4B5563] max-w-xl">
          Δημιουργήστε λογαριασμούς για τους συνεργάτες σας. Οι συνεργάτες έχουν πρόσβαση σε όλες τις λειτουργίες εκτός από τη διαχείριση χρηστών.
        </p>
        <button onClick={() => open()} className="btn-primary flex items-center gap-1.5" data-testid="add-user-btn">
          <PlusIcon size={16} /> Νέος Χρήστης
        </button>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9FAFB] border-b-2 border-black/20">
              {["Email", "Όνομα", "Ρόλος", "Πρόσβαση", "Δημιουργήθηκε", "Ενέργειες"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.1em] text-[#4B5563] font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-black/10 hover:bg-[#F9FAFB]" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono">{u.email}</td>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider ${u.role === "admin" ? "bg-[#374151] text-white" : u.role === "salesperson" ? "bg-[#6B7280] text-white" : "bg-[#0A0A0A] text-white"}`}>
                    {u.role === "admin" ? <ShieldIcon size={12} weight="fill" /> : <UserIcon size={12} weight="fill" />}
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[#4B5563]" data-testid={`user-perms-${u.email}`}>
                  {u.role === "admin"
                    ? "Όλα"
                    : `${((u.permissions?.length ? u.permissions : ROLE_DEFAULTS[u.role]) || []).length} ενότητες`}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#4B5563]">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("el-GR") : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => open(u)} className="p-1.5 hover:bg-black/5" data-testid={`edit-user-${u.email}`}><PencilSimpleIcon size={14} /></button>
                    <button
                      onClick={() => del(u)}
                      disabled={u.id === me?.id}
                      className="p-1.5 hover:bg-[#E60000] hover:text-white disabled:opacity-30"
                      data-testid={`delete-user-${u.email}`}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white border border-black/10 w-full max-w-md" data-testid="user-modal">
            <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
              <h3 className="heading font-bold">{editing ? "Επεξεργασία" : "Νέος Χρήστης"}</h3>
              <button type="button" onClick={() => setShow(false)} className="p-1 hover:bg-black/5"><XIcon size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              {!editing && (
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Email *</span>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" data-testid="user-email" />
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Όνομα *</span>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" data-testid="user-name" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">
                  Κωδικός {editing ? "(αφήστε κενό για να μην αλλάξει)" : "*"}
                </span>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={6} className="px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" data-testid="user-password" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Ρόλος</span>
                <select value={form.role} onChange={(e) => changeRole(e.target.value)} className="px-3 py-2 border border-black/20 outline-none text-sm" data-testid="user-role">
                  <option value="partner">Συνεργάτης (partner)</option>
                  <option value="salesperson">Πωλητής (salesperson)</option>
                  <option value="admin">Διαχειριστής (admin)</option>
                </select>
              </label>
              {form.role === "admin" ? (
                <div className="text-xs text-[#4B5563] bg-[#F9FAFB] border border-black/10 p-3">
                  Οι διαχειριστές βλέπουν πάντα όλες τις ενότητες.
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Τι βλέπει αυτός ο χρήστης</span>
                  <div className="border border-black/10 divide-y divide-black/5 max-h-56 overflow-y-auto" data-testid="user-permissions">
                    {PERMISSIONS.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F9FAFB]">
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(p.key)}
                          onChange={() => togglePerm(p.key)}
                          data-testid={`perm-${p.key}`}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-black/10 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="btn-secondary">Άκυρο</button>
              <button type="submit" className="btn-primary" data-testid="user-save">Αποθήκευση</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
